import { toaster } from '@/components/ui/toaster';
import { getConversationHref } from '@/pages/messaging/conversation-route';
import { useCompanionPanelStore } from '@/stores/companion-panel-store';
import { usePendingPayoutStore } from '@/stores/pending-payout-store';
import type {
  CompanionActionDto,
  CompanionCta,
  CompanionCtaWorkflow,
  CompanionEvidence,
  RecommendationBatchDto,
} from '@fluenceur/types';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type { NavigateFunction } from 'react-router-dom';

/**
 * Where each workflow leads, one entry per workflow.
 *
 * The destination is always built from this table, never from server text: a
 * stored card names a workflow, and the client decides where that leads. The
 * table is total over `CompanionCtaWorkflow`, so a workflow added to the
 * contract stops compiling here rather than reaching a brand as a card that
 * does nothing. A workflow this build has never heard of has no entry, and the
 * panel shows its call to action inert rather than sending the brand somewhere
 * approximate.
 */

/** Where the Discovery workflow lands, batch or no batch. */
export const DISCOVERY_RECOMMENDATIONS_PATH = '/creators/discovery?tab=recommendations';

/** The brand's own payout preparation, which is the only place a batch is made. */
export const PAYOUT_PREPARE_PATH = '/payments/commissions?tab=commissions';

/** The Candidatures page, where every application is answered. */
export const APPLICATIONS_PATH = '/applications';

/** How long the page is given to render the batch before scrolling to it. */
const SCROLL_ATTEMPT_DELAYS_MS = [120, 400, 1200];

/** What a handler is given: the card, and the ways this dashboard can move. */
export type CompanionCtaContext = {
  navigate: NavigateFunction;
  queryClient: QueryClient;
  t: TFunction;
  /** The same parameter the mention grids write, through nuqs. */
  setPreviewMention: (value: string | null) => Promise<unknown>;
  /** Called once the card is opened and the destination is decided. */
  onNavigated?: () => void;
};

/** A card whose call to action is known to name this workflow. */
type CompanionActionFor<W extends CompanionCtaWorkflow> = CompanionActionDto & {
  cta: Extract<CompanionCta, { workflow: W }>;
};

type CompanionCtaHandlers = {
  [W in CompanionCtaWorkflow]: (
    context: CompanionCtaContext,
    action: CompanionActionFor<W>
  ) => void | Promise<void>;
};

/**
 * Brings the batch into view once the Discovery tab has rendered it.
 *
 * A few attempts rather than one: the list is fetched after the route changes,
 * so the element does not exist yet at navigation time. Resolves to whether the
 * batch was ever found, which is what decides if the brand is told the list
 * they were sent to is not the one the card named.
 */
async function scrollToBatch(batchId: string): Promise<boolean> {
  if (typeof document === 'undefined') return false;

  const selector = `[data-batch-id="${CSS.escape(batchId)}"]`;
  for (const delay of SCROLL_ATTEMPT_DELAYS_MS) {
    const found = await new Promise<boolean>((resolve) => {
      window.setTimeout(() => {
        const element = document.querySelector(selector);
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        resolve(Boolean(element));
      }, delay);
    });
    if (found) return true;
  }

  return false;
}

/**
 * Whether the card names the batch Discovery is currently showing at the top.
 *
 * Read from the cache the Discovery tab already fills, per brand, so no extra
 * request is made just to decide where to scroll. An empty cache answers "not
 * the current one", which only costs a scroll attempt.
 */
function isCurrentBatch(queryClient: QueryClient, batchId: string): boolean {
  return queryClient
    .getQueriesData<RecommendationBatchDto>({ queryKey: ['recommendations'] })
    .some(([, batch]) => batch?.batchId === batchId);
}

/**
 * The name a bulk-email call to action opens the composer on, when the
 * card's evidence carries one. Two kinds share this workflow, each naming
 * its own subject through the same nullable `displayName` field, so a single
 * reader stands in for both rather than one branch per kind at the call site.
 */
function bulkEmailRecipientName(evidence: CompanionEvidence): string | null {
  return evidence.kind === 'affiliate_silent' || evidence.kind === 'gift_unclaimed'
    ? evidence.displayName
    : null;
}

const COMPANION_CTA_HANDLERS: CompanionCtaHandlers = {
  discovery: async (context, action) => {
    const { batchId } = action.cta;
    context.navigate(DISCOVERY_RECOMMENDATIONS_PATH);
    context.onNavigated?.();

    // The current batch is the first thing the tab shows, so there is nothing
    // to scroll to and nothing to warn about.
    if (isCurrentBatch(context.queryClient, batchId)) return;

    // Otherwise the card names an older batch. The brand still lands on the
    // right tab; they are only told when that batch is nowhere in the list,
    // so the page they see is explained rather than surprising.
    if (!(await scrollToBatch(batchId))) {
      toaster.create({
        type: 'info',
        description: context.t('companion.cta.discovery.batchMoved'),
      });
    }
  },

  bulk_email: (context, action) => {
    const { affiliateUserId, programId, draft } = action.cta;
    useCompanionPanelStore.getState().openEmailCta({
      actionId: action.id,
      affiliateUserId,
      programId,
      recipientName: bulkEmailRecipientName(action.evidence),
      draft,
    });
    context.onNavigated?.();
  },

  // The boost panel lives inside the publication's own preview, which any
  // page can open by naming the mention in the URL. The card opens it on the
  // publication it named and stops there: the request is the brand's to send.
  boost_request: async (context, action) => {
    await context.setPreviewMention(action.cta.mentionId);
    context.onNavigated?.();
  },

  // The thread the brand already has with this person. The card takes them
  // to it and writes nothing in it.
  conversation_reply: (context, action) => {
    context.navigate(getConversationHref(action.cta.conversationId, window.location.search));
    context.onNavigated?.();
  },

  // The composer the brand already uses, on the authorization the card
  // names, pre-filled with the same conditions for one month. It opens and
  // stops there: the request leaves when the brand presses send.
  display_rights_renewal: (context, action) => {
    const { authorizationId, terms } = action.cta;
    useCompanionPanelStore.getState().openDisplayRightsCta({
      actionId: action.id,
      authorizationId,
      creatorName:
        action.evidence.kind === 'rights_expiring' ? action.evidence.creatorDisplayName : null,
      initialTerms: {
        amountCents: terms.amountCents,
        durationMonths: terms.durationMonths,
      },
    });
    context.onNavigated?.();
  },

  program_invite: (context, action) => openInvitation(context, action, 'invite'),
  collab_proposal: (context, action) => openInvitation(context, action, 'collab'),

  // The payouts page the brand already uses, with the card's beneficiaries
  // filtered and ticked. Nothing is submitted: the page recomputes what
  // each of them is owed, and preparing the batch is the brand's own click.
  payout_prepare: (context, action) => {
    usePendingPayoutStore.getState().preselectFromCompanion({
      actionId: action.id,
      affiliateIds: action.cta.affiliateIds,
    });
    context.navigate(PAYOUT_PREPARE_PATH);
    context.onNavigated?.();
  },

  // The product in the brand's Shopify admin, in a new tab cut from the
  // dashboard. The address is the one the server wrote into the evidence, and
  // it is opened only when it is the Shopify admin: a card never sends a brand
  // anywhere else, whatever its evidence says.
  gift_stock_restock: (context, action) => {
    const url = action.evidence.kind === 'gift_stock' ? action.evidence.restockUrl : null;
    if (url && isShopifyAdminUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
    context.onNavigated?.();
  },

  // The Candidatures page the brand already uses: on the one application when
  // the card names exactly one, filtered on the brief when it groups several.
  // Answering stays the brand's own click there.
  brief_application_review: (context, action) => {
    const { applicationId, briefTemplateId } = action.cta;
    const query = applicationId
      ? new URLSearchParams({ c: applicationId })
      : new URLSearchParams({ targetIds: briefTemplateId });
    context.navigate(`${APPLICATIONS_PATH}?${query.toString()}`);
    context.onNavigated?.();
  },

  gift_reoffer: (context, action) => {
    useCompanionPanelStore.getState().openGiftReofferCta({
      actionId: action.id,
      version: action.version,
      giftRequestIds: action.cta.giftRequestIds,
    });
    context.onNavigated?.();
  },
};

/**
 * Whether an address is a product page of a Shopify admin: the unified admin,
 * or a shop's own `*.myshopify.com/admin`, over https only.
 */
function isShopifyAdminUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password) return false;
    if (url.hostname === 'admin.shopify.com') return true;
    return url.hostname.endsWith('.myshopify.com') && url.pathname.startsWith('/admin/');
  } catch {
    return false;
  }
}

/** The two invitation workflows differ by the step the wizard opens on. */
function openInvitation(
  context: CompanionCtaContext,
  action: CompanionActionFor<'program_invite'> | CompanionActionFor<'collab_proposal'>,
  mode: 'invite' | 'collab'
): void {
  useCompanionPanelStore.getState().openInvitationCta({
    actionId: action.id,
    mode,
    socialProfileId: action.cta.socialProfileId,
    creatorIdentityId:
      action.evidence.kind === 'mention_notable' ? action.evidence.creatorIdentityId : null,
    programId: action.cta.programId,
  });
  context.onNavigated?.();
}

/**
 * The workflows whose destination is a new tab. Their handler runs inside the
 * click, before anything is awaited, because a browser only lets a page open a
 * tab from the gesture itself; the opening is recorded after it.
 */
const COMPANION_CTA_OPENING_A_TAB: ReadonlySet<CompanionCtaWorkflow> = new Set([
  'gift_stock_restock',
]);

export const companionCtaOpensATab = (workflow: CompanionCtaWorkflow): boolean =>
  COMPANION_CTA_OPENING_A_TAB.has(workflow);

export const isCompanionCtaImplemented = (workflow: CompanionCtaWorkflow): boolean =>
  Object.prototype.hasOwnProperty.call(COMPANION_CTA_HANDLERS, workflow);

/**
 * The handler for a card, or nothing when the workflow it names has no
 * destination in this build. The cast is the one place the table's per-workflow
 * narrowing is traded for a single call site: the key it is read with is the
 * card's own workflow, so the handler and the card always agree.
 */
export const resolveCompanionCtaHandler = (
  action: CompanionActionDto
): ((context: CompanionCtaContext, action: CompanionActionDto) => void | Promise<void>) | null => {
  if (!isCompanionCtaImplemented(action.cta.workflow)) return null;
  return COMPANION_CTA_HANDLERS[action.cta.workflow] as (
    context: CompanionCtaContext,
    action: CompanionActionDto
  ) => void | Promise<void>;
};
